/**
 * The ChatForge wire protocol, mirrored from the server's `src/protocol.ts`.
 *
 * This file is the client's half of the contract and must be kept in step with
 * the server. `PROTOCOL_VERSION` is checked against the server's `welcome`
 * frame so a mismatch surfaces as a clear message rather than as fields that
 * are quietly undefined.
 */

export const PROTOCOL_VERSION = 9

export type User = {
  id: string
  /** The account login. Unique, and the only name a user has. */
  name: string
}

export type ImageMime = "image/png" | "image/jpeg" | "image/gif" | "image/webp"
/** A QuickTime upload comes back as MP4, which the server stores it as. */
export type VideoMime = "video/mp4" | "video/webm"
export type MediaMime = ImageMime | VideoMime

/**
 * An uploaded picture or video. `url` is server-relative, e.g.
 * `/uploads/<id>.png`; `width`/`height` are the upright display size.
 */
export type Attachment = {
  id: string
  url: string
  mime: MediaMime
  bytes: number
  width: number
  height: number
}

/** One emoji and everyone who put it on a message, in the order they did. */
export type Reaction = {
  emoji: string
  userIds: string[]
}

/**
 * The message a reply points at, as much of it as the quote above the reply
 * needs. The server reads it live rather than copying it at send time, so the
 * quote cannot drift, and it is null once the original is gone.
 */
export type ReplySnapshot = {
  id: string
  userId: string
  /** Author login of the quoted message. */
  name: string
  /** Empty when the quoted message is only pictures or videos. */
  text: string
  /** Whether the quoted message carries attachments, which the quote names. */
  hasAttachments: boolean
  /** Whether any of those attachments is a video. */
  hasVideo: boolean
}

export type Message = {
  id: string
  roomId: string
  userId: string
  /** Author login captured at send time. */
  name: string
  /** May be empty when the message is only pictures. */
  text: string
  attachments: Attachment[]
  /** Ordered by the moment each emoji first appeared on the message. */
  reactions: Reaction[]
  /** The message this one answers, or null for an ordinary message. */
  replyTo: ReplySnapshot | null
  at: number
  /** Present only on the sender's own copy, for reconciling an optimistic row. */
  nonce?: string
}

export type SystemEvent = {
  id: string
  roomId: string
  text: string
  at: number
}

/**
 * Every room is private to its members. A `group` is created by someone and
 * grows by invitation; a `direct` room is the one conversation between two
 * people and cannot be joined or left.
 */
export type RoomKind = "group" | "direct"

export type RoomSummary = {
  id: string
  kind: RoomKind
  /** For a direct room this is "a & b"; show the other participant instead. */
  name: string
  topic: string
  /** Everyone who can see the room, sorted by name. */
  participants: User[]
  /** People who have the room open right now. */
  memberCount: number
  lastMessage: Message | null
}

/**
 * One connection in a room's voice chat. `peerId` names the connection, not
 * the person: the same account on two devices is two peers, and the id is
 * new on every join.
 */
export type VoicePeer = {
  peerId: string
  user: User
  muted: boolean
}

/** An `RTCIceServer`: the TURN relay and its short-lived credentials. */
export type IceServer = {
  urls: string[]
  username?: string
  credential?: string
}

/** WebRTC negotiation between two peers, relayed by the server. */
export type VoiceSignal =
  | { kind: "offer" | "answer"; sdp: string }
  | {
      kind: "ice"
      candidate: {
        candidate: string
        sdpMid: string | null
        sdpMLineIndex: number | null
      }
    }

export type ClientFrame =
  | { type: "hello"; token: string }
  | { type: "open"; roomId: string; before?: string }
  | {
      type: "send"
      roomId: string
      text: string
      /** Ids from `POST /api/uploads`, at most `limits.maxAttachments`. */
      attachments: string[]
      nonce: string
      /** The id of a message in the same room this one answers. */
      replyTo: string | null
    }
  | { type: "typing"; roomId: string }
  /**
   * Puts `emoji` on a message, or takes the caller's copy of it off when
   * `remove` is set. Idempotent, so a replay after a reconnect is harmless.
   */
  | {
      type: "react"
      roomId: string
      messageId: string
      emoji: string
      remove?: boolean
    }
  /** Creates a group with the caller and `members` in it. */
  | { type: "createRoom"; name: string; topic?: string; members: string[] }
  /** Adds a user to a group the caller is in. */
  | { type: "invite"; roomId: string; userId: string }
  /** Removes the caller from a group; the room is deleted with its last member. */
  | { type: "leave"; roomId: string }
  | { type: "openDirect"; userId: string }
  /** Enters a room's voice chat; answered with `voiceJoined`. */
  | { type: "voiceJoin"; roomId: string }
  | { type: "voiceLeave"; roomId: string }
  | { type: "voiceMute"; roomId: string; muted: boolean }
  | { type: "voiceSignal"; roomId: string; to: string; signal: VoiceSignal }
  | { type: "pong" }

export type ServerFrame =
  | {
      type: "welcome"
      protocol: number
      self: User
      rooms: RoomSummary[]
      limits: {
        maxMessageLength: number
        historyPageSize: number
        maxUploadBytes: number
        /** Videos get their own, larger cap. */
        maxVideoUploadBytes: number
        maxAttachments: number
      }
    }
  | { type: "history"; roomId: string; messages: Message[]; atStart: boolean }
  | { type: "message"; message: Message }
  | { type: "system"; event: SystemEvent }
  | { type: "presence"; roomId: string; members: User[] }
  | { type: "typing"; roomId: string; user: User }
  /** The full reaction list of one message, after any change to it. */
  | {
      type: "reactions"
      roomId: string
      messageId: string
      reactions: Reaction[]
    }
  /** A room the recipient can now see: created, or invited into. */
  | { type: "roomCreated"; room: RoomSummary }
  | { type: "roomUpdated"; room: RoomSummary }
  /** The recipient can no longer see this room (they left it). */
  | { type: "roomRemoved"; roomId: string }
  /** Answer to `openDirect`: the direct room with that user, new or existing. */
  | { type: "direct"; room: RoomSummary }
  /**
   * Who is in a room's voice chat, after any change; empty means it is over.
   * `startedBy` is set only on the change that started it.
   */
  | {
      type: "voiceState"
      roomId: string
      peers: VoicePeer[]
      startedBy?: User
    }
  /** Answer to `voiceJoin`: your peer id, who to offer to, and how. */
  | {
      type: "voiceJoined"
      roomId: string
      peerId: string
      peers: VoicePeer[]
      iceServers: IceServer[]
    }
  | { type: "voiceSignal"; roomId: string; from: string; signal: VoiceSignal }
  /** The server took this connection out of a voice chat (it left the room). */
  | { type: "voiceLeft"; roomId: string }
  | { type: "ping" }
  | { type: "error"; code: ErrorCode; message: string }

export type ErrorCode =
  | "bad_frame"
  | "not_ready"
  | "unauthorized"
  | "unknown_room"
  | "unknown_user"
  | "unknown_attachment"
  | "unknown_message"
  | "forbidden"
  | "invalid_room_name"
  | "empty_message"
  | "rate_limited"
  | "voice_full"
  | "not_in_voice"

/**
 * Close code the server uses when a token is revoked by `/api/auth/logout`.
 * A client seeing it should return to the sign-in screen, not reconnect.
 */
export const CLOSE_LOGGED_OUT = 4001

/** The picture formats the server accepts; anything else is refused with 415. */
export const IMAGE_MIMES: readonly ImageMime[] = [
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]

export function isImageMime(value: string): value is ImageMime {
  return (IMAGE_MIMES as readonly string[]).includes(value)
}

/**
 * The video file types the server accepts. QuickTime is what an iPhone
 * records; it is ISO BMFF like MP4 and comes back as `video/mp4`.
 */
export const VIDEO_UPLOAD_TYPES: readonly string[] = [
  "video/mp4",
  "video/webm",
  "video/quicktime",
]

export function isVideoUploadType(value: string): boolean {
  return VIDEO_UPLOAD_TYPES.includes(value)
}

export function isVideo(attachment: Pick<Attachment, "mime">): boolean {
  return attachment.mime.startsWith("video/")
}

/** Account logins: short, URL- and mention-safe, case-insensitively unique. */
export const LOGIN_PATTERN = /^[A-Za-z0-9_]{3,32}$/

export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 128

export function isLogin(value: string): boolean {
  return LOGIN_PATTERN.test(value)
}

export function isPassword(value: string): boolean {
  return (
    value.length >= PASSWORD_MIN_LENGTH && value.length <= PASSWORD_MAX_LENGTH
  )
}

/**
 * Narrows an unknown parsed payload to a server frame. The socket is a network
 * boundary, so a frame is only trusted as far as having a string `type`; each
 * reducer case reads the fields it needs from there.
 */
export function isServerFrame(value: unknown): value is ServerFrame {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { type?: unknown }).type === "string"
  )
}
